import { missionLabel } from "../missions";
import { lang, t, tServer } from "./i18n";

/** Vocabulaire métier venu du serveur (noms de missions, spécialités, échelons…) dans la langue de l'interface.
 *  Les missions prennent le nom OTAN de l'ordre en anglais (`MISSIONS` de generex_engine/opord/english.py, même
 *  clé : majuscules sans accents) ; le reste passe par le dictionnaire (`t`), le français restant la clé. */

const MISSIONS_EN: Record<string, string> = {
  ATTAQUE: "Attack",
  ATTAQUER: "Attack",
  APPUYER: "Support",
  BARRER: "Block",
  BOUCLER: "Cordon off",
  "CONQUETE DE ZONE": "Area seizure",
  "CONTRE-ATTAQUER": "Counter-attack",
  "CONTRE ATTAQUER": "Counter-attack",
  "CONTRE-REBELLION": "Counter-insurgency",
  "CONTROLE DE ZONE": "Area control",
  "CONTROLER UNE ZONE": "Area control",
  "CONTROLER UN ITINERAIRE": "Route control",
  "CONTROLER UN SECTEUR": "Sector control",
  "COUP D'ARRET": "Stop",
  // COUP DE MAIN et RAID distincts en anglais (registre K12, `english.py`).
  "COUP DE MAIN": "Coup de main",
  COUVERTURE: "Covering operation",
  COUVRIR: "Cover",
  CTRZ: "Area control",
  DEFENDRE: "Defend",
  "DEFENSE D'USURE": "Attrition defence",
  "DEFENSE DE ZONE": "Area defence",
  "DEFENSE FERME": "Firm defence",
  DETRUIRE: "Destroy",
  ECLAIRER: "Screen",
  "EFFECTUER UN RAID": "Raid",
  "ESCORTE DE CONVOI": "Convoy escort",
  EXPLOITATION: "Exploitation",
  EXPLOITER: "Exploit",
  FIXER: "Fix",
  FREINER: "Delay",
  HARCELER: "Harass",
  INTERCEPTER: "Intercept",
  INTERDIRE: "Deny",
  JALONNER: "Track",
  "MARCHE A L'ENNEMI": "Advance to contact",
  "MENER UN RAID": "Deep raid",
  NEUTRALISER: "Neutralize",
  "PRENDRE LE CONTACT": "Gain contact",
  PROTEGER: "Protect",
  RAID: "Raid",
  RATISSER: "Sweep",
  "RECONNAISSANCE OFFENSIVE": "Reconnaissance in force",
  RECONNAITRE: "Recce",
  RECUEILLIR: "Assist rearward passage of lines",
  RELEVER: "Relieve",
  RENSEIGNER: "Gather information",
  // Unité réservée par le chef (`brigade_supports.MISSIONS`, réserve des appuis de la brigade).
  RESERVE: "Reserve",
  // Réserve d'une défense (décision D2 du 23/10/2026, `posture.RESERVE_VERB`).
  "CONSTITUER UNE RESERVE": "Form a reserve",
  RRI: "Clear",
  "ROT. CENTAC": "CENTAC rotation",
  "S'EMPARER": "Seize",
  "S'EMPARER DE": "Seize",
  "S'INFILTRER": "Infiltrate",
  // Niveau compagnie (docs/21 §5) : missions reçues du GTIA et missions des sections et pelotons.
  "MENER UN COUP DE MAIN": "Coup de main",
  "REDUIRE LES RESISTANCES ISOLEES": "Clear",
  REDUIRE: "Reduce",
  NETTOYER: "Clear",
  FOUILLER: "Search",
  ESCORTER: "Escort",
  CONTROLER: "Control",
  "SECURISATION DE ZONE": "Area security",
  SOUTENIR: "Sustain",
  SURVEILLER: "Monitor",
  TENIR: "Hold",
  // Modes d'action du corps d'armée (docs/08 §23, mémento p. 11-12), anglais OTAN britannique.
  "MANŒUVRE RETARDATRICE": "Delaying operation",
  INTERPOSITION: "Interposition",
  ASSISTANCE: "Assistance",
  // Mode d'assistance de la division et de la brigade (registre A97 ; mémento p. 12, BIA p. 83).
  "AIDE AUX POPULATIONS": "Assistance to the population",
  "APPUI A LA RECONSTRUCTION DE L'ETAT": "Support to state reconstruction",
};

/** Libellés français des codes canoniques du menu de mission (`mission_catalogue`, missions des gabarits : majuscules
 *  du gabarit, accents compris, « COUP D'ARRÊT »), par clé sans accents. Un code s'affiche comme un verbe
 *  (« Attaquer », « Coup de main ») ; RRI reste un sigle. */
const CODES_FR: Record<string, string> = {
  APPUYER: "Appuyer",
  ATTAQUER: "Attaquer",
  BARRER: "Barrer",
  "CONTRE-ATTAQUER": "Contre-attaquer",
  "COUP D'ARRET": "Coup d'arrêt",
  "COUP DE MAIN": "Coup de main",
  "CONSTITUER UNE RESERVE": "Constituer une réserve",
  COUVRIR: "Couvrir",
  CTRZ: "Contrôler une zone",
  DEFENDRE: "Défendre",
  DETRUIRE: "Détruire",
  ECLAIRER: "Éclairer",
  EXPLOITER: "Exploiter",
  FIXER: "Fixer",
  FREINER: "Freiner",
  INTERDIRE: "Interdire",
  JALONNER: "Jalonner",
  NEUTRALISER: "Neutraliser",
  RAID: "Raid",
  RECONNAITRE: "Reconnaître",
  RRI: "RRI",
  "S'EMPARER": "S'emparer",
  "S'INFILTRER": "S'infiltrer",
  SURVEILLER: "Surveiller",
  TENIR: "Tenir",
  // Missions des sections et pelotons (niveau compagnie, docs/21 §5), absentes des autres niveaux.
  BOUCLER: "Boucler",
  CONTROLER: "Contrôler",
  ESCORTER: "Escorter",
  FOUILLER: "Fouiller",
  NETTOYER: "Nettoyer",
  PROTEGER: "Protéger",
  RECUEILLIR: "Recueillir",
  REDUIRE: "Réduire",
};

/** Clé de mission de l'ordre en anglais : majuscules sans accents (« Coup d'arrêt » → « COUP D'ARRET »). */
export function missionKey(text: string): string {
  return text
    .trim()
    .replace(/’/g, "'")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, " ")
    .toUpperCase();
}

/** Nom affiché d'une mission : libellé français corrigé (`missionLabel`), ou nom OTAN en anglais. */
export function missionName(name: string | null | undefined): string {
  const label = missionLabel(name);
  if (!label) return label;
  if (lang() !== "en") {
    // Code canonique en majuscules (« ATTAQUER », « COUP DE MAIN ») : libellé du verbe ; le reste tel quel.
    const code = label === label.toUpperCase() ? CODES_FR[missionKey(label)] : undefined;
    return code ?? label;
  }
  // À défaut de la table de l'ordre, le dictionnaire de l'interface puis celui du serveur (libellés doctrinaux
  // CENTAC, « PRENDRE ET PRÉCISER LE CONTACT »…).
  return MISSIONS_EN[missionKey(name ?? "")] ?? MISSIONS_EN[missionKey(label)] ?? tServer(label);
}

/** Mission d'une unité (verbe de mission : ATTAQUER, FREINER, RECONNAÎTRE ; en anglais ATTACK, DELAY, RECCE),
 *  en capitales comme dans les ordres, accents conservés, partout où l'interface la montre (menu Manœuvre, fiches,
 *  menu de mission, carte, cohérence). Affichage seul : le code enregistré et les comparaisons (`missionKey`,
 *  `sameMission`) n'en dépendent pas. Les missions du dossier (« Marche à l'ennemi ») gardent `missionName`. */
export function unitMissionName(name: string | null | undefined): string {
  return missionName(name).toLocaleUpperCase(lang() === "en" ? "en-GB" : "fr-FR");
}

/** Terme du vocabulaire (spécialité, échelon, catégorie…) : traduit par le dictionnaire, sinon tel quel. */
export function term(value: string | null | undefined): string {
  return value ? t(value) : "";
}
